import React, { useEffect, useState, useContext, Suspense } from 'react';
import { fetchDemographicsData } from '../../../services/Http/Api';
import { useTranslation } from 'react-i18next';
import { SettingsContext } from '../../SettingsContext';
import styled from 'styled-components';
import StyledCard from '../../../shared/layout/cards/StyledCard';

// Dynamically import ReactApexChart
const ReactApexChart = React.lazy(() => import('react-apexcharts'));

const TransactionsStatistics = ({ period }) => {
    const { t } = useTranslation();
    const { siteSettings } = useContext(SettingsContext);
    const [demographicsData, setDemographicsData] = useState({
        gender: {},
        ageGroups: {},
        cities: {},
        countries: {},
        residentialCountries: {}
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadDemographicsData = async () => {
            setLoading(true);
            try {
                const data = await fetchDemographicsData({ period });
                setDemographicsData({
                    gender: data.gender_summary,
                    ageGroups: data.age_group_summary,
                    cities: data.city_summary,
                    countries: data.country_summary,
                    residentialCountries: data.residential_country_summary
                });
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        loadDemographicsData();
    }, [period]);

    if (loading) {
        return <LoadingContainer>{t('Transactions.TransactionsStatistics.loading')}</LoadingContainer>;
    }

    if (error) {
        return <Error>{t('Transactions.TransactionsStatistics.error', { error: error.message })}</Error>;
    }

    const countries = siteSettings?.getAllCountries() || [];

    const resolveCountryLabel = (code) => {
        if (!code) {
            return t('common.none');
        }

        const normalized = String(code).toLowerCase();
        const match = countries.find((country) => {
            return (
                String(country.id).toLowerCase() === normalized ||
                (country.iso && country.iso.toLowerCase() === normalized) ||
                (country.iso3 && country.iso3.toLowerCase() === normalized) ||
                (country.numcode && String(country.numcode).toLowerCase() === normalized) ||
                (country.name && country.name.toLowerCase() === normalized)
            );
        });

        return match ? match.name : String(code).toUpperCase();
    };

    const buildCountryEntries = (summary = {}) =>
        Object.entries(summary)
            .map(([key, value]) => ({
                label: resolveCountryLabel(key),
                value: Number(value) || 0,
            }))
            .filter((entry) => entry.value > 0);

    const countryEntries = buildCountryEntries(demographicsData.countries);
    const residentialEntries = buildCountryEntries(demographicsData.residentialCountries);

    const brandColors = ['#821692', '#3fc380', '#17a2b8', '#ffc107', '#dc3545', '#007bff'];

    const getBaseOptions = (title) => ({
        chart: {
            toolbar: { show: false },
            fontFamily: 'Inter, system-ui, sans-serif'
        },
        colors: brandColors,
        stroke: { show: false },
        dataLabels: {
            enabled: true,
            style: { fontSize: '12px', fontWeight: 600 }
        },
        legend: {
            position: 'bottom',
            fontSize: '13px',
            fontFamily: 'Inter, sans-serif',
            labels: { colors: '#64748b' }
        },
        title: {
            text: title,
            style: {
                fontSize: '16px',
                fontWeight: 600,
                color: '#1e293b'
            }
        }
    });

    const genderChartData = {
        series: Object.values(demographicsData.gender),
        options: {
            ...getBaseOptions(t('Transactions.TransactionsStatistics.genderDistribution')),
            labels: Object.keys(demographicsData.gender).map(g => t(`common.gender.${g.toLowerCase()}`, g)),
            responsive: [{ breakpoint: 768, options: { chart: { height: 250 } } }],
        },
    };

    const ageGroupChartData = {
        series: [{ name: t('Transactions.TransactionsStatistics.ageGroupDistribution'), data: Object.values(demographicsData.ageGroups) }],
        options: {
            ...getBaseOptions(t('Transactions.TransactionsStatistics.ageGroupDistribution')),
            chart: { ...getBaseOptions().chart, type: 'bar', height: 350 },
            xaxis: {
                categories: Object.keys(demographicsData.ageGroups),
                labels: { style: { colors: '#64748b' } }
            },
            yaxis: { labels: { style: { colors: '#64748b' } } },
            plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
            dataLabels: { enabled: false }
        },
    };

    const countryChartData = {
        series: countryEntries.map((entry) => entry.value),
        options: {
            ...getBaseOptions(t('Transactions.TransactionsStatistics.countryDistribution')),
            labels: countryEntries.map((entry) => entry.label),
            responsive: [{ breakpoint: 768, options: { chart: { height: 250 } } }],
        },
    };

    const residentialCountryChartData = {
        series: [
            {
                name: t('Transactions.TransactionsStatistics.residentialCountryDistribution'),
                data: residentialEntries.map((entry) => entry.value),
            },
        ],
        options: {
            ...getBaseOptions(t('Transactions.TransactionsStatistics.residentialCountryDistribution')),
            chart: { ...getBaseOptions().chart, type: 'bar', height: 350 },
            xaxis: {
                categories: residentialEntries.map((entry) => entry.label),
                labels: { style: { colors: '#64748b' } }
            },
            yaxis: { labels: { style: { colors: '#64748b' } } },
            plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
            dataLabels: { enabled: false }
        },
    };

    const cityChartData = {
        series: [{ name: t('Transactions.TransactionsStatistics.cityDistribution'), data: Object.values(demographicsData.cities) }],
        options: {
            ...getBaseOptions(t('Transactions.TransactionsStatistics.cityDistribution')),
            chart: { ...getBaseOptions().chart, type: 'bar', height: 350 },
            xaxis: {
                categories: Object.keys(demographicsData.cities),
                labels: { style: { colors: '#64748b' } }
            },
            yaxis: { labels: { style: { colors: '#64748b' } } },
            plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
            dataLabels: { enabled: false }
        },
    };

    return (
        <Container>
            <ChartsGrid>
                {genderChartData.series.length > 0 && (
                    <StyledCard>
                        <Suspense fallback={<Loading>Loading Chart...</Loading>}>
                            <ReactApexChart options={genderChartData.options} series={genderChartData.series} type="pie" height={350} />
                        </Suspense>
                    </StyledCard>
                )}

                {countryChartData.series.length > 0 && (
                    <StyledCard>
                        <Suspense fallback={<Loading>Loading Chart...</Loading>}>
                            <ReactApexChart options={countryChartData.options} series={countryChartData.series} type="pie" height={350} />
                        </Suspense>
                    </StyledCard>
                )}
            </ChartsGrid>

            {ageGroupChartData.series[0].data.length > 0 && (
                <StyledCard>
                    <Suspense fallback={<Loading>Loading Chart...</Loading>}>
                        <ReactApexChart options={ageGroupChartData.options} series={ageGroupChartData.series} type="bar" height={350} />
                    </Suspense>
                </StyledCard>
            )}

            {cityChartData.series[0].data.length > 0 && (
                <StyledCard>
                    <Suspense fallback={<Loading>Loading Chart...</Loading>}>
                        <ReactApexChart options={cityChartData.options} series={cityChartData.series} type="bar" height={350} />
                    </Suspense>
                </StyledCard>
            )}

            {residentialCountryChartData.series[0].data.length > 0 && (
                <StyledCard>
                    <Suspense fallback={<Loading>Loading Chart...</Loading>}>
                        <ReactApexChart options={residentialCountryChartData.options} series={residentialCountryChartData.series} type="bar" height={350} />
                    </Suspense>
                </StyledCard>
            )}
        </Container>
    );
};

export default TransactionsStatistics;

const Container = styled.div``;

const ChartsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
    margin-bottom: 0px;

    @media (max-width: 992px) {
        grid-template-columns: 1fr;
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    color: #64748b;
    font-size: 0.9375rem;
`;

const Loading = styled.div`
    text-align: center;
    font-size: 0.875rem;
    color: #64748b;
`;

const Error = styled.div`
    text-align: center;
    font-size: 1rem;
    color: #ef4444;
    padding: 2.5rem;
`;

